<template>
  <div>
    <PageHeader :items="items" />
    <validation-observer ref="simpleRules">
      <div class="card">
        <div class="card-body">
          <b-row>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <validation-provider
                #default="{ errors }"
                name="Start Date"
                rules="required"
              >
                <DatePicker
                  v-model="form.startDate"
                  :label="$t('startDate')"
                  :required="true"
                  :isLeftIcon="true"
                  :leftIconName="'dateIcon'"
                  :error="errors[0]"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <validation-provider
                #default="{ errors }"
                name="Due Date"
                rules="required"
              >
                <DatePicker
                  v-model="form.dueDate"
                  :label="$t('dueDate')"
                  :required="true"
                  :isLeftIcon="true"
                  :leftIconName="'dateIcon'"
                  :min="minDate"
                  :error="errors[0]"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <label class="input-label form-label"
                >{{ $t("type") }}&nbsp;<span style="color: red">*</span>
              </label>
              <b-form-group class="mb-0">
                <select class="form-control" v-model="form.type">
                  <option
                    v-for="option in typeOptions"
                    :value="option.value"
                    :key="option.value"
                  >
                    {{ $t(option.text) }}
                  </option>
                </select>
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <validation-provider
                #default="{ errors }"
                :name="form.type === 'coins' ? 'freeCredits' : 'freeCases'"
                rules="required"
              >
                <TextInput 
                  v-model="form.freeUsers"
                  type="number"
                  :label="
                    $t(form.type === 'coins' ? 'freeCredits' : 'freeCases')
                  "
                  :required="true"
                  :error="errors[0]"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <validation-provider
                #default="{ errors }"
                name="Amount of activations"
                rules="required"
              >
                <TextInput
                  v-model="form.amountOfActivations"
                  type="number"
                  :label="$t('amountOfActivations')"
                  :required="true"
                  :error="errors[0]"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <div class="form-group mt-1">
                <label class="form-label" for="cars">{{
                  $t("instantlyGrantCredits/cases")
                }}</label>

                <input
                  id="isInstantGrant"
                  class="form-check-input-custom ml-2"
                  v-model="form.isInstantGrant"
                  type="checkbox"
                />
              </div>
            </b-col>
            <b-col cols="12" lg="6" md="6" class="mb-1">
              <TextInput
                v-model="form.createdUser"
                type="text"
                :label="$t('creator')"
                :required="false"
                :disabled="true"
              />
            </b-col>
            <b-col cols="12">
              <label class="input-label form-label" for="cars">{{
                $t("notes")
              }}</label>
              <textarea class="form-control" rows="5" v-model="form.notes" />
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-end gap-2">
        <router-link
          to="/coupon-codes"
          class="d-flex align-items-center gap-1 btn btn-secondary"
        >
          <feather-icon icon="XIcon" size="12" />
          <span>{{ $t("cancel") }}</span>
        </router-link>
        <b-button
          variant="primary"
          @click="createCoupon"
          class="d-flex align-items-center gap-1"
          :disabled="isFormInvalid"
          v-if="$can(`${$route.meta.permission}.create`)"
        >
          <feather-icon icon="PlusIcon" size="12" />
          <span>{{ $t("createCoupon") }}</span>
        </b-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import PageHeader from "@/components/PageHeader.vue";
import MultiSelect from "vue-multiselect";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mapGetters } from "vuex";
import DatePicker from "@/components/DatePicker.vue";
import { required, email } from "@validations";
import formDirtyMixin from "@/mixins/formDirtyMixin";

export default {
  components: {
    TextInput,
    DatePicker,
    PageHeader,
    MultiSelect,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [formDirtyMixin],
  computed: {
    ...mapGetters("auth", ["user"]),
    minDate() {
      return new Date().toISOString().split("T")[0]; // Format as 'YYYY-MM-DD'
    },
    items() {
      return [
        {
          text: "Dental Twin",
          to: "/home",
        },
        {
          text: this.$t("couponCodes"),
          to: "/coupon-codes",
        },
        {
          text: this.$t("create"),
          active: true,
        },
      ];
    },
    isFormInvalid() {
      return !this.form.dueDate || !this.form.freeUsers || !this.form.startDate || !this.form.amountOfActivations;
    },
  },
  watch: {
    form: {
      handler(newVal) {
        this.isFormDirty = JSON.stringify(newVal) !== this.originalForm;
      },
      deep: true,
    },
  },
  data() {
    return {
      originalForm: null,
      form: {
        startDate: "",
        isInstantGrant: false,
        amountOfActivations: "",
        dueDate: "",
        type: "coins",
        freeUsers: "",
        createdUser: "",
        notes: "",
      },
      typeOptions: [
        { value: "coins", text: "credits" },
        { value: "cases", text: "cases" },
      ],
    };
  },
  async mounted() {
    this.form.dueDate = this.calculateFutureDate(30);
    this.form.createdUser = this.user.first_name + " " + this.user.last_name;
    this.originalForm = JSON.stringify(this.form);
  },
  methods: {
    calculateFutureDate(days) {
      const today = new Date();
      const futureDate = new Date(today);
      futureDate.setDate(today.getDate() + days);

      // Format the date as YYYY-MM-DD
      const year = futureDate.getFullYear();
      const month = String(futureDate.getMonth() + 1).padStart(2, "0");
      const day = String(futureDate.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    async createCoupon() {
      this.$refs.simpleRules.validate().then(async (success) => {
        if (success) {
          await this.$store.dispatch("coupons/create", {
            ...this.form,
          });

          this.isFormDirty = false;
          this.originalForm = JSON.stringify(this.form);
          this.$router.push("/coupon-codes");
        }
      });
    },
  },
};
</script>

<style scoped>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>
